import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Block,
  Checkbox,
  Heading,
  Paragraph,
  RadioButton,
  RadioButtonGroup
} from 'suomifi-ui-components';
import { FormattedMessage } from 'react-intl';
import { formTitle } from './schema';
import {
  AddContactButton,
  IntermediaryTextField,
  PersonFields
} from '../../components';
import styled from 'styled-components';
import { userObject } from '../IntermediaryOrganisationContacts';
import { Link } from 'react-router';

const StyledPersonContainer = styled.div`
  border-bottom: 1px solid bg-border-color(border);
  background: #f7fafd;
  padding: 0.25rem 1.5rem 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #c8cdd0;
`;
export const Form = ({ formik, serviceType }, { intl }) => {
  const i18n = id => intl.formatMessage({ id });
  const webInvoiceEnabled =
    formik?.values?.formData?.organisationBillingInfo?.webInvoice;
  const billingDisabled =
    formik.values.formData.organisationBillingInfoDisabled;
  const [
    billingContactBackupPersonVisible,
    setBillingContactBackupPersonVisible
  ] = useState(false);

  React.useEffect(() => {
    const showBillingBackupPerson = Object.values(
      formik.values?.formData?.organisationBillingInfo
        ?.billingContactBackupPerson || {}
    )?.some(v => v?.length);
    setBillingContactBackupPersonVisible(showBillingBackupPerson);
  }, [
    formik.values.formData.organisationBillingInfo?.billingContactBackupPerson
  ]);

  const clearBillingContactBackupPerson = () => {
    formik.setFieldValue(
      'formData.organisationBillingInfo.billingContactBackupPerson',
      { ...userObject }
    );
    setBillingContactBackupPersonVisible(false);
  };
  return (
    <>
      <Block mb="l">
        <Heading variant="h2" className="borderless mb-2">
          <FormattedMessage id={formTitle} />
        </Heading>
        <Paragraph>
          <FormattedMessage id="intermediary.organisationBillingInfo.heading" />
        </Paragraph>
      </Block>
      <Block>
        <Paragraph>
          <StyledPersonContainer>
            <Checkbox
              checked={billingDisabled}
              onClick={({ checkboxState }) => {
                formik.setFieldValue(
                  'formData.organisationBillingInfoDisabled',
                  checkboxState
                );
              }}
            >
              {serviceType === 'eIdentification' ? (
                <FormattedMessage id="organization.intermediary.organisation_billing_not_warranted_eidentification" />
              ) : (
                <FormattedMessage id="organization.intermediary.organisation_billing_not_warranted_eauthorization" />
              )}
              <Link
                to={
                  serviceType === 'eIdentification'
                    ? i18n(
                        'organization.intermediary.organisation_billing_info_eidentification_link'
                      )
                    : i18n(
                        'organization.intermediary.organisation_billing_info_eauthorization_link'
                      )
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="organization.intermediary.organisation_billing_not_warranted_link" />
              </Link>
            </Checkbox>

            {!billingDisabled && (
              <>
                <IntermediaryTextField
                  name={'formData.organisationBillingInfo.organizationName'}
                  label={
                    'intermediary.organisationBillingInfo.organisationName'
                  }
                  width={'80%'}
                />
                <IntermediaryTextField
                  name={'formData.organisationBillingInfo.referenceNumber'}
                  label={'intermediary.organisationBillingInfo.referenceNumber'}
                  width={'40%'}
                  optional={true}
                  tooltip={
                    <FormattedMessage id="intermediary.organisationBillingInfo.referenceNumberInfo" />
                  }
                />
                <IntermediaryTextField
                  name={'formData.organisationBillingInfo.vatNumber'}
                  label={'intermediary.organisationBillingInfo.vatNumber'}
                  width={'40%'}
                />

                <RadioButtonGroup
                  className="mt-4"
                  name={'formData.organisationBillingInfo.webInvoice'}
                  value={webInvoiceEnabled?.toString()}
                  onChange={value =>
                    formik.setFieldValue(
                      'formData.organisationBillingInfo.webInvoice',
                      value === 'true'
                    )
                  }
                  labelText={
                    <FormattedMessage id="intermediary.organisationBillingInfo.invoiceTypeTitle" />
                  }
                >
                  <RadioButton value={'true'}>
                    <FormattedMessage id="intermediary.organisationBillingInfo.webInvoiceEnabled" />
                  </RadioButton>
                  <RadioButton value={'false'}>
                    <FormattedMessage id="intermediary.organisationBillingInfo.webInvoiceDisabled" />
                  </RadioButton>
                </RadioButtonGroup>
                {webInvoiceEnabled ? (
                  <>
                    <IntermediaryTextField
                      width={'40%'}
                      name={
                        'formData.organisationBillingInfo.webInvoiceHandlerIdentifier'
                      }
                      label={
                        'intermediary.organisationBillingInfo.webInvoiceHandlerIdentifier'
                      }
                    />
                    <IntermediaryTextField
                      width={'40%'}
                      name={
                        'formData.organisationBillingInfo.webInvoiceAddress'
                      }
                      label={
                        'intermediary.organisationBillingInfo.webInvoiceAddress'
                      }
                    />
                  </>
                ) : (
                  <>
                    <IntermediaryTextField
                      name={'formData.organisationBillingInfo.address'}
                      label={'intermediary.organisationBillingInfo.address'}
                      width={'40%'}
                    />
                    <IntermediaryTextField
                      name={'formData.organisationBillingInfo.postalCode'}
                      label={'general.form.zip_code'}
                      width={'40%'}
                    />
                    <IntermediaryTextField
                      name={'formData.organisationBillingInfo.postOffice'}
                      label={'general.form.city'}
                      width={'40%'}
                    />
                  </>
                )}
                <Heading variant="h4">
                  <FormattedMessage id="intermediary.organisationBillingInfo.billingContactPerson" />
                </Heading>
                <PersonFields
                  name="formData.organisationBillingInfo.billingContactPerson"
                  useContainer={false}
                />
                {billingContactBackupPersonVisible ? (
                  <>
                    <Heading variant="h4">
                      <FormattedMessage id="intermediary.organisationBillingInfo.billingContactBackupPerson" />
                    </Heading>
                    <PersonFields
                      name="formData.organisationBillingInfo.billingContactBackupPerson"
                      onDelete={() => clearBillingContactBackupPerson()}
                      useContainer={false}
                    />
                  </>
                ) : (
                  <AddContactButton
                    setVisible={() =>
                      setBillingContactBackupPersonVisible(true)
                    }
                  />
                )}
              </>
            )}
          </StyledPersonContainer>
        </Paragraph>
      </Block>
    </>
  );
};

Form.propTypes = {
  formik: PropTypes.object.isRequired,
  serviceType: PropTypes.string.isRequired
};
Form.contextTypes = {
  intl: PropTypes.object
};

export default Form;
